import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ConfigService } from '../../config/config.service';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { lastValueFrom } from 'rxjs';

declare type TokenData = {
    refresh_at: any;
    access_token: any;
    refresh_token: any;
}

@Injectable({
    providedIn: 'root'
})
export class LoginService {
    private _loginUri: string = '';
    private _code: string = null;

    public async isLoggedIn(): Promise<boolean> {
        return await this.getToken() != null;
    }

    constructor(private http: HttpClient, private conf: ConfigService, private route: ActivatedRoute, private router: Router, protected localStorage: StorageMap) {
        // Construct login uri
        this._loginUri = `https://${this.conf.backend.baseUri}/${this.conf.backend.loginPath}?client_id=${this.conf.backend.clientId}&response_type=code&redirect_uri=${this.conf.backend.redirectUri}`;

        // Subscribe to router events to grab query string 'code' param
        this.router.events.subscribe(async e => {
            if (e instanceof NavigationEnd) {

                // console.log('Navigation end');

                // Get code query param from route snapshot
                this._code = this.route.snapshot.queryParams['code'];

                await this.getToken();
            }
        });
    }

    private async requestToken(code: string): Promise<any> {
        const uri = `https://${this.conf.backend.baseUri}/${this.conf.backend.tokenPath}`;

        const httpHeaders = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded'
            })
        };

        const httpBody = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('client_id', this.conf.backend.clientId)
            .set('code', code)
            .set('redirect_uri', this.conf.backend.redirectUri)
            .set('client_secret', this.conf.backend.clientSecret);

        return await lastValueFrom(this.http.post(uri, httpBody, httpHeaders));
    }

    public async logIn(): Promise<void> {
        // console.log('Start login');

        // if (await this.getToken()) return;
        // console.log('Token undefined');

        // Navigate to login window to get a authorization code
        await new Promise<void>(res => {
            this.localStorage.delete(`${this.conf.backend.clientId}-TOKEN`).subscribe(() => {
                res();
            })
        })
        window.open(this._loginUri, '_self');
    }

    private _flyingGetTokenRequest: Promise<string> = null;

    public async getToken(): Promise<string> {
        if (!this._flyingGetTokenRequest) this._flyingGetTokenRequest = this._getToken();

        return this._flyingGetTokenRequest;
    }

    private async setToken(token: any): Promise<any> {
        // console.log(`Writing to local storage`);

        if (!token || !token.expires_in || !token.access_token || !token.refresh_token) throw new Error('Invalid token');

        // Convert token ttl to expiration time
        const t = {
            refresh_at: Math.floor((new Date()).valueOf() / 1000 - 10 * 60) + token.expires_in,
            access_token: token.access_token,
            refresh_token: token.refresh_token
        };

        this.localStorage.set(`${this.conf.backend.clientId}-TOKEN`, t).subscribe(() => {
            // console.log('TOKEN SET');
        });

        return t;
    }

    private async _getToken(): Promise<string> {
        return new Promise<string>((res) => {
            this.localStorage.get(`${this.conf.backend.clientId}-TOKEN`).subscribe(async (token: TokenData) => {
                if (!token || !this.validateToken(token)) {
                    // Is code present to retrieve token?
                    if (this._code) {
                        // Try to get token
                        // console.log('Code found');

                        try {
                            const newToken: any = await this.requestToken(this._code);

                            await this.setToken(newToken);

                            res(newToken.access_token);

                        } catch (err) {
                            // console.log(err);
                            if (err.error instanceof Error) {
                                // A client-side or network error occurred.				 
                                // console.log('An error occurred:', err.error.message);
                            } else {
                                // Backend returns unsuccessful response codes such as 404, 500 etc.
                                // Redirect to login page
                                this._flyingGetTokenRequest = null;
                                this.logIn();
                                res(null);
                            }
                        }
                    } else {
                        // console.log('No code');
                        // Redirect to login page
                        this.logIn();
                        this._flyingGetTokenRequest = null;
                        res(null);
                    }
                }

                // Check expiration
                // else if (token.resfresh_at && Math.floor(new Date().valueOf()) / 1000 > token.refresh_at) {
                //// console.log('Refresh token');
                else if (Math.floor(new Date().valueOf() / 1000) > token.refresh_at) {
                    // console.log('Refresh token');
                    try {
                        const refreshResponse = await this.refreshToken(token.refresh_token);
                        // Add refresh token to response so it is stored locally
                        refreshResponse.refresh_token = token.refresh_token;
                        const newToken = await this.setToken(refreshResponse);
                        this._flyingGetTokenRequest = null;
                        if (!this.validateToken(newToken)) return null;
                        res(newToken.access_token);
                    } catch (err) {
                        // console.log('Error with refreshing token');
                        // clear token getter
                        this._flyingGetTokenRequest = null;

                        // When refresh fails, clear local cache and login
                        await new Promise<void>((res2) => {
                            this.localStorage.clear().subscribe(() => {
                                // console.log('LOCAL STORAGE CLEARED');

                                res2();
                            });

                            this.logIn();
                            // console.log(err);
                        });

                        res(null);
                    }
                }

                // console.log(`Token found!`);
                this._flyingGetTokenRequest = null;

                res(token?.access_token ?? '');
            });
        })

        // console.log(token);
        // console.log('Get token');
        // console.log(`code = ${this._code}`);

        // Check for all required properties

    }

    private validateToken(token: any): boolean {
        return Boolean(token && token.access_token && token.refresh_at && token.refresh_token);
    }

    private async refreshToken(refreshToken: string): Promise<any> {
        const uri = `https://${this.conf.backend.baseUri}/${this.conf.backend.tokenPath}`;

        const httpHeaders = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded'
            })
        };

        const httpBody = new HttpParams()
            .set('grant_type', 'refresh_token')
            .set('client_id', this.conf.backend.clientId)
            .set('refresh_token', refreshToken)
            .set('client_secret', this.conf.backend.clientSecret);

        return lastValueFrom(this.http.post(uri, httpBody, httpHeaders));
    }
}
